/**
 * Minimal JWT payload reader. No signature check: the server verifies, the
 * client only needs `exp`.
 *
 * The token stored by the app carries the "JWT " scheme prefix (the API sets
 * the cookie as `JWT <token>` and expects the same value back in the
 * Authorization header), so the prefix is tolerated here.
 */

export type JwtPayload = Record<string, unknown> & { exp?: number };

const base64UrlDecode = (input: string): string => {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  // Handle UTF-8 payloads (user names with accents).
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

export const stripScheme = (token: string): string =>
  token.replace(/^JWT\s+/i, '').trim();

export const decodeJwtPayload = (
  token: string | null | undefined,
): JwtPayload | null => {
  if (!token) return null;
  const parts = stripScheme(token).split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const payload: unknown = JSON.parse(base64UrlDecode(parts[1]));
    return payload && typeof payload === 'object'
      ? (payload as JwtPayload)
      : null;
  } catch {
    return null;
  }
};

/**
 * No token -> expired, no `exp` claim -> never expires.
 */
export const isTokenExpired = (
  token: string | null | undefined,
  now: number = Date.now(),
): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  if (typeof payload.exp !== 'number') return false;
  return payload.exp * 1000 <= now;
};
